import { HttpErrorResponse } from '@angular/common/http';

/**
 * Error the HTTP interceptors throw onward once the failure has already been
 * reported to the user. Carries the resolved message plus the original
 * response, so an outer interceptor (or a caller) can react without toasting
 * the same failure a second time.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number = 0,
    public readonly response?: HttpErrorResponse
  ) {
    super(message);
    this.name = 'ApiError';
    // Required when targeting ES5 so `instanceof ApiError` keeps working.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// Body keys the backends in use actually put their message under.
// BusinessInsights (ASP.NET ProblemDetails) uses `detail`; the legacy OPS API
// uses `msg`/`message`; the token endpoint uses `error_description`.
const MESSAGE_KEYS = [
  'detail',
  'msg',
  'message',
  'error_description',
  'exceptionMessage',
  'description',
  'title',
  'error',
];

/**
 * The message the server actually sent, or '' when the response carries none
 * (network failure, empty 400 body, HTML error page).
 */
export function getBackendErrorMessage(error: HttpErrorResponse): string {
  let body: any = error?.error;

  if (typeof body === 'string') {
    const text = body.trim();
    if (!text) return '';
    try {
      body = JSON.parse(text);
    } catch {
      // Plain-text body is usable; an HTML error page is not.
      return text.startsWith('<') ? '' : text;
    }
  }

  if (!body || typeof body !== 'object') return '';
  // ProgressEvent => request never reached the server, nothing to read.
  // Must be an instanceof check, not a `.type` check: ProblemDetails bodies
  // carry their own `type` field alongside the `detail` we want.
  if (typeof Event !== 'undefined' && body instanceof Event) return '';

  // ProblemDetails validation payload: { errors: { Field: ['msg', ...] } }
  const validation = body.errors ?? body.Errors;
  if (validation && typeof validation === 'object') {
    const messages = Object.keys(validation)
      .reduce((all: any[], key) => all.concat(validation[key]), [])
      .filter((m: any) => typeof m === 'string' && m.trim());
    if (messages.length) return messages.join(' ');
  }

  for (const key of MESSAGE_KEYS) {
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    const value = body[key] ?? body[pascalKey];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  return '';
}

export function resolveHttpErrorMessage(error: HttpErrorResponse): string {
  return getBackendErrorMessage(error);
}

/** OAuth `invalid_grant` => the refresh/password grant was refused. */
export function isInvalidGrant(error: HttpErrorResponse): boolean {
  return error?.error?.error === 'invalid_grant';
}
